import { chatDrawerActions, chatDrawerSelectors } from 'ducks/chatDrawer'
import { wsSelectors } from 'ducks/websocket'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

export const getLastMessage = (messages: any[]): any | undefined => messages[messages.length - 1]

export const getLastMessageIndex = (messages: any[]): number | null => {
  if (!messages || messages.length === 0) return null
  return messages.length - 1
}

export const hasUnrespondedMessage = (messages): boolean => {
  const lastMessage = getLastMessage(messages)
  return lastMessage?.direction === 'send'
}

export const hasResponseStarted = (messages, isStreaming): boolean => {
  return isStreaming || (messages.length > 1 && getLastMessage(messages)?.direction === 'receive')
}

export const isAwaitingResponse = (messages, isStreaming): boolean => {
  const lastMessage = getLastMessage(messages)
  if (lastMessage?.origin === 'voice') {
    return false
  }
  return hasUnrespondedMessage(messages) && !hasResponseStarted(messages, isStreaming)
}

export const useRetryOnReconnect = (messages, orgId, conversationId, connectionId) => {
  const websocketAvailable = useSelector(wsSelectors.getAvailable)
  const isStreaming = useSelector(chatDrawerSelectors.getStreaming)
  const dispatch = useDispatch()
  const prevWsStateRef = useRef<{ available: boolean; connectionId: string | number } | null>(null)
  const [retryPending, setRetryPending] = useState<{ messageIndex: number } | null>(null)

  useEffect(() => {
    const prev = prevWsStateRef.current

    const websocketCameBackOnline = websocketAvailable && prev?.available === false
    const connectionIdChanged = connectionId && prev?.connectionId && prev.connectionId !== connectionId
    const reconnected = websocketCameBackOnline || connectionIdChanged

    if (reconnected && isAwaitingResponse(messages, isStreaming)) {
      const messageIndex = getLastMessageIndex(messages)
      if (messageIndex === null) return
      setRetryPending({ messageIndex })
      dispatch(chatDrawerActions.markMessageIndexFailed(messageIndex))
    }

    prevWsStateRef.current = { available: !!websocketAvailable, connectionId: connectionId as any }
  }, [websocketAvailable, connectionId, messages, isStreaming, dispatch])

  useEffect(() => {
    if (!retryPending) return

    const stillAwaiting = isAwaitingResponse(messages, isStreaming)
    const currentLastIndex = getLastMessageIndex(messages)

    // Clear retry if we're awaiting a response to a different message than the retry was for
    // This happens when user sends a new message instead of retrying
    if (stillAwaiting && retryPending.messageIndex !== currentLastIndex) {
      setRetryPending(null)
      return
    }

    // Clear retry pending if we're no longer awaiting a response
    if (!stillAwaiting) {
      setRetryPending(null)
    }
  }, [messages, isStreaming, retryPending])

  return retryPending
}

export const shouldShowRetryBanner = (retryPending, websocketAvailable): boolean => {
  return !!retryPending && websocketAvailable
}

export const shouldShowLoader = (messages, isStreaming, adaStatus, retryPending, websocketAvailable): boolean => {
  const lastMessage = messages[messages.length - 1]
  if (lastMessage?.origin === 'voice') {
    return false
  }

  const awaitingResponse = isAwaitingResponse(messages, isStreaming)
  const processingWithStatus =
    hasUnrespondedMessage(messages) && adaStatus && !hasResponseStarted(messages, isStreaming)
  return (awaitingResponse || processingWithStatus) && !retryPending && websocketAvailable
}
